import { resolveApiBaseUrl, usablePublicMapToken } from '@croissants/ui';
/**
 * Everything this app reads from configuration, in one module.
 *
 * No absolute URL is ever written as a literal in a screen (PRD OPS-018): the API
 * origin, the payment provider's publishable key and the legal-page links all
 * derive from `import.meta.env`, so the move from croissantinbed.com to
 * croissantinbed.com is a configuration change and not a code change.
 */

import {
  type LegalDocumentId,
  legalDocument,
  legalPath,
  surfaceDocuments,
} from '@croissants/contracts/legal';
import type { Locale } from '@croissants/i18n';

function trimmed(value: string | undefined): string {
  return (value ?? '').trim();
}

/** The proxy path `vite.config.ts` forwards to the configured API origin. */
export const DEV_PROXY_PREFIX = '/api';

/**
 * The API origin, through the one resolver every portal shares
 * (`resolveApiBaseUrl`, @croissants/ui, F6). In the dev server an unset or
 * cross-origin value becomes the same-origin `/api` proxy (vite.config.ts
 * forwards it, to the local worker when nothing is configured); a build uses
 * the configured value verbatim, and no host is hardcoded either way (PRD
 * OPS-018) - this file's own `http://localhost:8787` fallback is gone.
 */
export const API_BASE_URL: string = resolveApiBaseUrl({
  configured: import.meta.env.VITE_API_BASE_URL,
  dev: import.meta.env.DEV,
  origin: globalThis.location?.origin,
  devProxyPrefix: DEV_PROXY_PREFIX,
});

/** Public by design; paired with the secret key held only by the worker. */
export const STRIPE_PUBLISHABLE_KEY: string = trimmed(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

/**
 * The map token for the live courier map (PRD GST-035): a PUBLIC token by the one
 * rule every portal applies (`usablePublicMapToken`, `@croissants/ui`), or null.
 * Null means the order page shows the courier's facts without a map instead of a
 * grey rectangle.
 */
export const MAPBOX_PUBLIC_TOKEN: string | null = usablePublicMapToken(
  import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN,
);

/**
 * The public site that owns the privacy policy, the legal notice and the contact
 * page (PRD WEB-007). Derived from the configured domain rather than hardcoded,
 * and left absent rather than guessed: a wrong link is worse than none.
 */
const PUBLIC_SITE = trimmed(import.meta.env.VITE_PUBLIC_SITE_URL);

export interface LegalLinks {
  /** Never null: the terms are this app's own page, not the public site's. */
  readonly terms: string;
  /** Never null either: the "how it works" page (L. 111-7) is this app's own too. */
  readonly howItWorks: string;
  readonly privacy: string | null;
  readonly legal: string | null;
}

/**
 * The contact page the public site publishes, per locale. Named here rather
 * than in the legal register because it is not a legal document; the French
 * page is `/contact` and the English one `/en/contact`, and
 * `website/tools/build-pages.py` is the only authority on those names.
 *
 * It took the locale late: it once answered `/contact` - the FRENCH page - for
 * an English guest, on all three screens that offer it. `site-links.test.ts`
 * resolves every link against the files the site serves for that reason.
 */
const CONTACT_PATHS: Record<Locale, string> = { fr: '/contact', en: '/en/contact' };

/**
 * Every legal document this app links comes from ONE register,
 * `@croissants/contracts/legal`: which documents the storefront lists (its
 * `order` surface), on which host and at which path per language, and which
 * version each prints. The terms of sale and the "how it works" page are
 * served HERE, on the host the guest is actually on; the privacy policy and the
 * legal notice are the public site's. A document the register puts on another
 * audience's surface cannot reach this footer - `legal.test.ts` refuses it.
 */
export const GUEST_LEGAL_ROUTES = surfaceDocuments('order')
  .filter((d) => d.host === 'order')
  .flatMap((d) =>
    (['fr', 'en'] as const).map((locale) => ({
      id: d.id as 'guest-terms' | 'guest-how-it-works',
      locale,
      path: legalPath(d.id as LegalDocumentId, locale).path,
    })),
  );

/** Where a document is read in a locale: this app's own path, or the public site's URL. */
function legalHref(id: LegalDocumentId, locale: Locale): string | null {
  const { host, path } = legalPath(id, locale);
  return host === 'order' ? path : siteUrl(path);
}

/**
 * `null` rather than a guess when no public site is configured: a screen that
 * offers a dead link is worse than one that offers none, which is why every
 * caller has a control that works without it.
 */
function siteUrl(path: string): string | null {
  if (PUBLIC_SITE === '') return null;
  return `${PUBLIC_SITE.replace(/\/+$/, '')}${path}`;
}

export function legalLinks(locale: Locale): LegalLinks {
  return {
    terms: legalHref('guest-terms', locale) as string,
    howItWorks: legalHref('guest-how-it-works', locale) as string,
    privacy: legalHref('privacy', locale),
    legal: legalHref('legal-notice', locale),
  };
}

/**
 * The contact route the public site publishes (PRD WEB-008), for the screens
 * that have to hand a guest onwards - an address we cannot serve yet, above all.
 *
 * It takes the locale for the same reason the legal links do: the site publishes
 * `/contact` in French and `/en/contact` in English, and a guest reading the
 * storefront in English should not be handed a French page to write from.
 */
export function contactLink(locale: Locale): string | null {
  return siteUrl(CONTACT_PATHS[locale]);
}

/**
 * The version of the terms the guest accepts at checkout (PRD WEB-007), recorded
 * on the order. It is the register's, which is the date printed at the head of
 * the terms themselves (`legal.test.ts` and `documents.test.ts` hold the three
 * equal): an order recording `2026-08-19` has to be able to point at the
 * wording that carried that date, and a version no published page ever showed
 * is a consent record that proves nothing.
 */
export const TERMS_VERSION: string = legalDocument('guest-terms').version;
